<script lang="ts">import { cfgToThemeCdnPath, stateUi } from "@infinitro/config";
import { toastStore } from "@infinitro/global";
import { CloseIcon, LocalIcon } from "@infinitro/icons";
import { fly } from "svelte/transition";
import ErrorIcon from "./icons/error.svg";
import InfoIcon from "./icons/info.svg";
import SuccessIcon from "./icons/success.svg";
import WarningIcon from "./icons/warning.svg";
let toasts = [];
toastStore.subscribe((value) => toasts = value);
const toastClasses = (type) => {
	switch (type) {
		case "success": return "toast-success";
		case "error": return "toast-error";
		case "info": return "toast-info";
		case "warning": return "toast-warning";
		default: return "toast-default";
	}
};
const fallbackIcon = {
	success: SuccessIcon,
	error: ErrorIcon,
	info: InfoIcon,
	warning: WarningIcon
};
const themedIcon = {
	success: cfgToThemeCdnPath("/icons/success.svg"),
	error: cfgToThemeCdnPath("/icons/error.svg"),
	info: cfgToThemeCdnPath("/icons/info.svg"),
	warning: cfgToThemeCdnPath("/icons/warning.svg")
};
const themedLocalIcon = {
	success: "SuccessIcon",
	error: "ErrorIcon",
	info: "InfoIcon",
	warning: "WarningIcon"
};
$: toastsStyle = $stateUi.toasts?.toastsStyle ?? "default";
$: isLocalIcon = $stateUi.toasts?.isLocalIcon ?? false;
const getToastClass = (type) => {
	const baseClass = toastClasses(type);
	if (toastsStyle === "alternative" || toastsStyle === "alternative2") {
		return `${baseClass}-${toastsStyle}`;
	}
	return baseClass;
};
const onToastIconError = (event, type) => {
	const target = event.currentTarget;
	if (!target || target.src === fallbackIcon[type]) return;
	target.src = fallbackIcon[type];
};
</script>

<div class="toast-container">
	{#each toasts as { id, message, description, type, action, actionTitle, secondaryAction, secondaryActionTitle } (id)}
		<!-- svelte-ignore a11y-click-events-have-key-events -->
		<!-- svelte-ignore a11y-no-static-element-interactions -->
		<div
			class="toast {getToastClass(type)}"
			in:fly={{ y: -10, duration: 200 }}
			out:fly={{ y: 10, duration: 200 }}
			on:click={() => !action && !secondaryAction && toastStore.removeToast(id)}
		>
			<div class="icon-container">
				{#if type && fallbackIcon[type]}
					{#if isLocalIcon}
						<LocalIcon icon={themedLocalIcon[type]} size={16} />
					{:else}
						<img
							src={themedIcon[type]}
							alt={type + ' icon'}
							class="toast-icon"
							on:error={(event) => onToastIconError(event, type)}
						/>
					{/if}
				{/if}
			</div>
			<div class="toast-content">
				<div class="toast-title">{message}</div>
				{#if description}
					<div class="toast-description">{description}</div>
				{/if}
				<div class="toast-actions">
					{#if action && actionTitle}
						<button
							class="toast-action-button"
							on:click={() => {
								action();
								toastStore.removeToast(id);
							}}
						>
							{actionTitle}
						</button>
					{/if}
					{#if secondaryAction && secondaryActionTitle}
						<button
							class="toast-secondary-action-button"
							on:click={() => {
								secondaryAction();
								toastStore.removeToast(id);
							}}
						>
							{secondaryActionTitle}
						</button>
					{/if}
				</div>
			</div>
			<div class="close-icon-container" on:click={() => toastStore.removeToast(id)}>
				{#if isLocalIcon}
					<LocalIcon icon="CloseIcon" size={12} />
				{:else}
					<CloseIcon />
				{/if}
			</div>
		</div>
	{/each}
</div>

<style>
	.toast-container {
		position: fixed;
		top: 1rem;
		right: 1rem;
		z-index: 50;
}

.toast-container > :not([hidden]) ~ :not([hidden]) {
		--tw-space-y-reverse: 0;
		margin-top: calc(1rem * calc(1 - var(--tw-space-y-reverse)));
		margin-bottom: calc(1rem * var(--tw-space-y-reverse));
}

.toast-container {
		font-family: var(--font-main);
		z-index: 1000;
}

	.toast {
		display: flex;
		min-width: 200px;
		max-width: 90vw;
		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
		align-items: flex-start;
		gap: 0.125rem;
		border-radius: 8px;
		padding: 0.5rem;
		--tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
		--tw-shadow-colored: 0 10px 15px -3px var(--tw-shadow-color), 0 4px 6px -4px var(--tw-shadow-color);
		box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
		transition-property: all;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 150ms;
}

	@media (min-width: 1024px) {

		.toast {
				max-width: 300px;
		}
}

	.toast-content {
		flex-grow: 1;
		overflow-wrap: break-word;
		padding-left: 0.25rem;
		padding-right: 0.25rem;
}

	.toast-success {
		background-color: var(--component-success-background);
		color: var(--component-success-text);
}

	.toast-error {
		background-color: var(--component-error-background);
		color: var(--component-error-text);
}

	.toast-info {
		background-color: var(--component-info-background);
		color: var(--component-info-text);
}

	.toast-warning {
		background-color: var(--component-warning-background);
		color: var(--component-warning-text);
}

	.toast-success-alternative,
	.toast-error-alternative,
	.toast-info-alternative,
	.toast-warning-alternative,
	.toast-default-alternative {
		min-height: 100px;
		min-width: 300px;
		max-width: 90vw;
		border-width: 1px;
		color: var(--color-text-100);
		background: radial-gradient(
			100% 100% at 0% 50%,
			var(--color-tertiary-300) 0%,
			var(--color-tertiary-700) 100%
		);
}

	.toast-success-alternative {
		border-color: var(--color-success-500);
}

	.toast-error-alternative {
		border-color: var(--color-error-500);
}

	.toast-info-alternative {
		border-color: var(--color-info-500);
}

	.toast-warning-alternative {
		border-color: var(--color-warning-500);
}

	.toast-default-alternative {
		border-color: var(--color-neutral-900);
}

	.toast-success-alternative .toast-title {
		color: var(--color-success-500);
}

	.toast-error-alternative .toast-title {
		color: var(--color-error-500);
}

	.toast-info-alternative .toast-title {
		color: var(--color-info-500);
}

	.toast-warning-alternative .toast-title {
		color: var(--color-warning-500);
}

	.toast-default-alternative .toast-title {
		color: var(--color-text-100);
}

	.toast-success-alternative .toast-description,
	.toast-error-alternative .toast-description,
	.toast-info-alternative .toast-description,
	.toast-warning-alternative .toast-description,
	.toast-default-alternative .toast-description {
		color: var(--color-text-300);
}

	.toast-success-alternative2,
	.toast-error-alternative2,
	.toast-info-alternative2,
	.toast-warning-alternative2,
	.toast-default-alternative2 {
		background-color: var(--color-background-900);
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		border-width: 1px;
		border-color: var(--color-neutral-900);
}

	.toast-default {
		--tw-bg-opacity: 1;
		background-color: rgb(31 41 55 / var(--tw-bg-opacity, 1));
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	.toast-title {
		font-size: 14px;
		font-weight: 700;
}

	.toast-description {
		margin-top: 0.125rem;
		font-size: 14px;
}

	.toast-actions {
		margin-top: 0.5rem;
		display: flex;
}

	.toast-actions > :not([hidden]) ~ :not([hidden]) {
		--tw-space-x-reverse: 0;
		margin-right: calc(1rem * var(--tw-space-x-reverse));
		margin-left: calc(1rem * calc(1 - var(--tw-space-x-reverse)));
}

	.toast-action-button {
		background-color: transparent;
		font-size: 14px;
		font-weight: 600;
		text-decoration-line: underline;
}

	.toast-secondary-action-button {
		background-color: transparent;
		font-size: 14px;
}

	.toast-success-alternative .toast-action-button,
	.toast-error-alternative .toast-action-button,
	.toast-info-alternative .toast-action-button,
	.toast-warning-alternative .toast-action-button,
	.toast-default-alternative .toast-action-button,
	.toast-success-alternative .toast-secondary-action-button,
	.toast-error-alternative .toast-secondary-action-button,
	.toast-info-alternative .toast-secondary-action-button,
	.toast-warning-alternative .toast-secondary-action-button,
	.toast-default-alternative .toast-secondary-action-button {
		color: var(--color-text-100);
		text-decoration-line: none;
}

	.icon-container {
		flex-shrink: 0;
}

	.close-icon-container {
		flex-shrink: 0;
		cursor: pointer;
		color: inherit;
}

	.close-icon-container :global(svg path) {
		fill: currentColor;
	}

	.toast-success .toast-icon,
	.toast-error .toast-icon,
	.toast-info .toast-icon,
	.toast-warning .toast-icon {
		color: inherit;
	}
</style>
